(() => {
  const setPlayButtonState = (hero, playing) => {
    const playBtn = hero.querySelector('[data-pilates-hero-play]');
    if (!playBtn) return;

    const playIcon = playBtn.querySelector('.pilates-hero__play-icon--play');
    const pauseIcon = playBtn.querySelector('.pilates-hero__play-icon--pause');
    const playLabel = playBtn.dataset.labelPlay;
    const pauseLabel = playBtn.dataset.labelPause;

    if (playIcon) playIcon.hidden = playing;
    if (pauseIcon) pauseIcon.hidden = !playing;
    if (playLabel && pauseLabel) {
      playBtn.setAttribute('aria-label', playing ? pauseLabel : playLabel);
    }
    playBtn.setAttribute('aria-pressed', playing ? 'true' : 'false');
    hero.classList.toggle('is-playing', playing);
  };

  const playHeroVideo = (hero) => {
    const isMobile = window.matchMedia('(max-width: 1024px)').matches;
    const hostedDesktop = hero.querySelector('.pilates-hero__bg-video--desktop');
    const hostedMobile = hero.querySelector('.pilates-hero__bg-video--mobile');
    const hosted = isMobile ? hostedMobile || hostedDesktop : hostedDesktop || hostedMobile;
    const iframe = hero.querySelector('[data-pilates-hero-video-iframe]');

    if (hosted) {
      hosted.muted = true;
      hosted.loop = true;
      hosted.controls = false;
      const playPromise = hosted.play();
      if (playPromise && typeof playPromise.catch === 'function') {
        playPromise.catch(() => {});
      }
    } else if (iframe) {
      const provider = isMobile
        ? iframe.dataset.pilatesVideoProviderMobile || iframe.dataset.pilatesVideoProviderDesktop
        : iframe.dataset.pilatesVideoProviderDesktop || iframe.dataset.pilatesVideoProviderMobile;
      if (provider === 'youtube') {
        const id = isMobile
          ? iframe.dataset.pilatesYoutubeIdMobile || iframe.dataset.pilatesYoutubeIdDesktop
          : iframe.dataset.pilatesYoutubeIdDesktop || iframe.dataset.pilatesYoutubeIdMobile;
        if (!id) return;
        iframe.src = `https://www.youtube.com/embed/${id}?autoplay=1&mute=1&loop=1&playlist=${id}&controls=0&playsinline=1&iv_load_policy=3&disablekb=1&rel=0&modestbranding=1`;
      } else if (provider === 'vimeo') {
        const id = isMobile
          ? iframe.dataset.pilatesVimeoIdMobile || iframe.dataset.pilatesVimeoIdDesktop
          : iframe.dataset.pilatesVimeoIdDesktop || iframe.dataset.pilatesVimeoIdMobile;
        if (!id) return;
        iframe.src = `https://player.vimeo.com/video/${id}?background=1&autoplay=1&loop=1&muted=1&controls=0`;
      }
    }

    setPlayButtonState(hero, true);
  };

  const pauseHeroVideo = (hero) => {
    const hosted = hero.querySelectorAll('.pilates-hero__bg-video');
    const iframe = hero.querySelector('[data-pilates-hero-video-iframe]');

    if (hosted.length > 0) {
      hosted.forEach((video) => video.pause());
    }
    if (iframe) {
      iframe.removeAttribute('src');
    }

    setPlayButtonState(hero, false);
  };

  const toggleHeroVideo = (hero) => {
    if (hero.classList.contains('is-playing')) {
      pauseHeroVideo(hero);
    } else {
      playHeroVideo(hero);
    }
  };

  const syncNavMenuState = (nav) => {
    if (!nav) return;
    const hasOpenDropdown = nav.querySelector('[data-pilates-nav-dropdown][open]');
    nav.classList.toggle('is-menu-open', Boolean(hasOpenDropdown));
  };

  const initNavDropdowns = (hero, nav) => {
    const dropdowns = Array.from(hero.querySelectorAll('[data-pilates-nav-dropdown]'));
    if (dropdowns.length === 0) return;

    dropdowns.forEach((dropdown) => {
      dropdown.addEventListener('toggle', () => {
        if (dropdown.open) {
          dropdowns.forEach((other) => {
            if (other !== dropdown) other.removeAttribute('open');
          });
        }
        syncNavMenuState(nav);
      });
    });

    document.addEventListener('click', (event) => {
      dropdowns.forEach((dropdown) => {
        if (!dropdown.contains(event.target)) {
          dropdown.removeAttribute('open');
        }
      });
      syncNavMenuState(nav);
    });

    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape') return;
      dropdowns.forEach((dropdown) => dropdown.removeAttribute('open'));
      syncNavMenuState(nav);
    });
  };

  const initNavAnchorHighlight = (hero) => {
    const links = Array.from(hero.querySelectorAll('[data-pilates-nav-anchor]'));
    if (links.length === 0) return;

    const sections = links
      .map((link) => {
        const id = link.getAttribute('href')?.replace('#', '');
        if (!id) return null;
        const section = document.getElementById(id);
        return section ? { link, section } : null;
      })
      .filter(Boolean);

    if (sections.length === 0) return;

    const setActive = (activeLink) => {
      links.forEach((link) => {
        link.closest('.pilates-hero__dropdown-item')?.classList.toggle(
          'is-active',
          link === activeLink
        );
      });
    };

    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver(
        (entries) => {
          const visible = entries
            .filter((entry) => entry.isIntersecting)
            .sort((a, b) => b.intersectionRatio - a.intersectionRatio);

          if (visible.length === 0) return;

          const match = sections.find(({ section }) => section === visible[0].target);
          if (match) setActive(match.link);
        },
        { rootMargin: '-40% 0px -45% 0px', threshold: [0, 0.25, 0.5, 0.75, 1] }
      );

      sections.forEach(({ section }) => observer.observe(section));
    }
  };

  document.querySelectorAll('[data-pilates-hero]').forEach((hero) => {
    const nav = hero.querySelector('[data-pilates-sticky-nav]');
    const sentinel = hero.querySelector('[data-pilates-nav-sentinel]');
    const slot = hero.querySelector('[data-pilates-nav-slot]');
    const toggle = hero.querySelector('[data-pilates-menu-toggle]');
    const panel = hero.querySelector('[data-pilates-mobile-menu]');
    const playBtn = hero.querySelector('[data-pilates-hero-play]');
    const mobileNavQuery = window.matchMedia('(max-width: 1024px)');
    const isMobileNav = () => mobileNavQuery.matches;

    const mountNavToBody = () => {
      if (!nav || !slot || nav.parentElement === document.body) return;
      document.body.appendChild(nav);
    };

    const restoreNavToSlot = () => {
      if (!nav || !slot || nav.parentElement === slot) return;
      slot.appendChild(nav);
    };

    const resetNav = () => {
      if (!nav || !slot) return;
      nav.classList.remove('is-stuck', 'is-fixed', 'is-menu-open');
      restoreNavToSlot();
      slot.style.height = '';
      hero.querySelectorAll('[data-pilates-nav-dropdown]').forEach((dropdown) => {
        dropdown.removeAttribute('open');
      });
      if (toggle && panel) {
        toggle.setAttribute('aria-expanded', 'false');
        panel.hidden = true;
      }
    };

    let navInteractionsReady = false;

    const ensureNavInteractions = () => {
      if (navInteractionsReady) return;
      initNavDropdowns(hero, nav);
      if (!isMobileNav()) {
        initNavAnchorHighlight(hero);
      }
      navInteractionsReady = true;
    };

    let hideNavTimer = null;
    let hideNavListener = null;

    const clearHideNavTransition = () => {
      if (hideNavTimer) {
        window.clearTimeout(hideNavTimer);
        hideNavTimer = null;
      }
      if (hideNavListener) {
        nav.removeEventListener('transitionend', hideNavListener);
        hideNavListener = null;
      }
    };

    const finalizeHideNav = () => {
      clearHideNavTransition();
      nav.classList.remove('is-fixed');
      restoreNavToSlot();
      slot.style.height = '';
    };

    const setStuck = (stuck) => {
      if (!nav || !slot) return;

      if (stuck) {
        clearHideNavTransition();

        if (!nav.classList.contains('is-fixed')) {
          mountNavToBody();
          slot.style.height = `${nav.offsetHeight}px`;
          nav.classList.add('is-fixed');
          requestAnimationFrame(() => {
            requestAnimationFrame(() => {
              if (window.scrollY >= window.innerHeight) {
                nav.classList.add('is-stuck');
              }
            });
          });
          return;
        }

        nav.classList.add('is-stuck');
        return;
      }

      if (!nav.classList.contains('is-fixed')) return;

      nav.classList.remove('is-stuck');
      clearHideNavTransition();

      hideNavListener = (event) => {
        if (event.target !== nav || event.propertyName !== 'transform') return;
        finalizeHideNav();
      };

      nav.addEventListener('transitionend', hideNavListener);
      hideNavTimer = window.setTimeout(finalizeHideNav, 380);
    };

    const syncByScreen = () => {
      ensureNavInteractions();
      const threshold = window.innerHeight;
      setStuck(window.scrollY >= threshold);
    };

    syncByScreen();
    window.addEventListener('scroll', syncByScreen, { passive: true });
    window.addEventListener('resize', syncByScreen);
    mobileNavQuery.addEventListener('change', syncByScreen);

    if (hero.dataset.autoplayVideo === 'true') {
      playHeroVideo(hero);
    }

    if (playBtn) {
      playBtn.addEventListener('click', () => {
        toggleHeroVideo(hero);
      });
    }

    hero.addEventListener('click', (event) => {
      const btn = event.target.closest('[data-pilates-ga-cta]');
      if (!btn) return;
      if (window.PilatesAnalytics && typeof window.PilatesAnalytics.pushLearnMore === 'function') {
        const params = {
          itemName: btn.dataset.pilatesGaCta || '',
          section: btn.dataset.pilatesGaSection || '',
        };
        window.PilatesAnalytics.pushLearnMore(params);
      }
    });

    if (!toggle || !panel) return;

    toggle.addEventListener('click', () => {
      if (isMobileNav()) return;
      const open = toggle.getAttribute('aria-expanded') === 'true';
      toggle.setAttribute('aria-expanded', open ? 'false' : 'true');
      panel.hidden = open;
    });
  });
})();
